import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProduct } from '../../hooks/useProducts';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { wishlistService } from '../../services/api';
import { getProductImageUrl } from '../../utils/imageHelper';
import ProductReviews from '../ProductReviews/ProductReviews';
import styles from './ProductDetail.module.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { product, isLoading, error } = useProduct(id);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { addNotification } = useNotifications();
  const [mainImage, setMainImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkingFavorite, setCheckingFavorite] = useState(false);

  // Vérifier si le produit est dans les favoris
  useEffect(() => {
    if (isAuthenticated && product?.id) {
      checkFavoriteStatus();
    }
  }, [isAuthenticated, product?.id]);

  const checkFavoriteStatus = async () => {
    try {
      setCheckingFavorite(true);
      const response = await wishlistService.checkWishlist(product.id);
      if (response.success) {
        setIsFavorite(response.data?.is_favorite || false);
      }
    } catch (error) {
      console.warn('Erreur lors de la vérification des favoris:', error);
    } finally {
      setCheckingFavorite(false);
    }
  };


  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        const response = await wishlistService.removeFromWishlist(product.id);
        if (response.success) {
          setIsFavorite(false);
          addNotification('Produit retiré des favoris', 'info');
        } else {
          addNotification(response.message || 'Erreur lors de la suppression', 'error');
        }
      } else {
        const response = await wishlistService.addToWishlist(product.id);
        if (response.success) {
          setIsFavorite(true);
          addNotification('Produit ajouté aux favoris', 'success');
        } else {
          addNotification(response.message || 'Erreur lors de l\'ajout', 'error');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la gestion des favoris:', error);
      addNotification('Erreur lors de la gestion des favoris', 'error');
    }
  };

  // Mettre à jour l'image principale quand le produit change
  useEffect(() => {
    if (product) {
      const primaryImage = product.primary_image
        ? getProductImageUrl(product.primary_image)
        : (product.images?.[0] ? getProductImageUrl(product.images[0]) : null);
      if (primaryImage) {
        setMainImage(primaryImage);
      }
    }
  }, [product]);

  // Obtenir toutes les images du produit
  const getAllImages = () => {
    if (!product) return [];
    const images = [];

    // Image principale
    if (product.primary_image) {
      const primaryImgUrl = getProductImageUrl(product.primary_image);
      if (primaryImgUrl) {
        images.push(primaryImgUrl);
      }
    }

    // Autres images
    if (product.images && product.images.length > 0) {
      product.images.forEach(img => {
        const imgUrl = getProductImageUrl(img);
        if (imgUrl && !images.includes(imgUrl)) {
          images.push(imgUrl);
        }
      });
    }

    // Si aucune image, utiliser un placeholder
    if (images.length === 0) {
      images.push('/images/leaves-right.png');
    }

    return images;
  };

  const handleAddToCart = async () => {
    if (!product) return;

    try {
      const result = await addToCart(product.id, quantity);
      if (result && result.success) {
        setAddedToCart(true);
        addNotification(`${product.name} ajouté au panier`, 'success');
        setTimeout(() => setAddedToCart(false), 2000);
      } else {
        addNotification(result?.error || 'Erreur lors de l\'ajout au panier', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      addNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.productDetail}>
        <div className={styles.loading}>
          <p>Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={styles.productDetail}>
        <div className={styles.error}>
          <h2>Produit non trouvé</h2>
          <p>{error || 'Le produit demandé n\'existe pas ou n\'est plus disponible.'}</p>
          <button onClick={() => navigate('/')} className={styles.backButton}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const images = getAllImages();
  // Priorité: promotional_price (promotion admin) > sale_price (promotion directe) > price
  const basePrice = parseFloat(product.price || 0);
  const directSalePrice = product.sale_price ? parseFloat(product.sale_price) : null;
  const promotionalPrice = product.promotional_price ? parseFloat(product.promotional_price) : null;

  const currentPrice = promotionalPrice || directSalePrice || basePrice;
  const oldPrice = promotionalPrice ? (directSalePrice || basePrice) : (directSalePrice && directSalePrice < basePrice ? basePrice : null);
  const discountPercentage = oldPrice && oldPrice > currentPrice ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100) : 0;
  const isOnSale = (oldPrice !== null && oldPrice > currentPrice) || (product.best_promotion !== null);
  const isOutOfStock = product.stock_quantity <= 0;

  return (
    <div className={styles.productDetail}>
      <button className={styles.backButton} onClick={() => navigate(-1)}>
        ← Retour à l'accueil
      </button>
      <div className={styles.productContainer}>
        <div className={styles.productImageSection}>
          {images.length > 1 && (
            <div className={styles.relatedImages}>
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`${product.name} - Vue ${index + 1}`}
                  className={`${styles.relatedImage} ${mainImage === img ? styles.active : ''}`}
                  onClick={() => setMainImage(img)}
                  onError={(e) => {
                    e.target.src = '/images/leaves-right.png';
                  }}
                />
              ))}
            </div>
          )}
          <div className={styles.productImage}>
            <img
              src={mainImage || images[0]}
              alt={product.name}
              onError={(e) => {
                e.target.src = '/images/leaves-right.png';
              }}
            />
            {isAuthenticated && (
              <button
                className={styles.favoriteButton}
                onClick={handleToggleFavorite}
                title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                disabled={checkingFavorite}
              >
                <svg
                  className={styles.heartIcon}
                  fill={isFavorite ? "currentColor" : "none"}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className={styles.productInfo}>
          <div className={styles.productHeader}>
            <h1 className={styles.productTitle}>{product.name}</h1>
            {isAuthenticated && (
              <button
                className={styles.favoriteButtonDetail}
                onClick={handleToggleFavorite}
                title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                disabled={checkingFavorite}
              >
                <svg
                  className={styles.heartIconDetail}
                  fill={isFavorite ? "currentColor" : "none"}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {isFavorite ? 'Retiré des favoris' : 'Ajouter aux favoris'}
              </button>
            )}
          </div>

          {product.category && (
            <div className={styles.productCategory}>
              <span className={styles.categoryBadge}>{product.category.name}</span>
            </div>
          )}

          <div className={styles.productPrice}>
            {isOnSale && discountPercentage > 0 && (
              <span className={styles.promotionBadge}>
                {product.best_promotion ? `🎁 ${product.best_promotion.name}` : `-${discountPercentage}%`}
              </span>
            )}
            <span className={styles.currentPrice}>{currentPrice.toLocaleString('fr-FR')} FCFA</span>
            {oldPrice && oldPrice > currentPrice && (
              <span className={styles.oldPrice}>{oldPrice.toLocaleString('fr-FR')} FCFA</span>
            )}
            {product.best_promotion && product.best_promotion.description && (
              <div className={styles.promotionDescription}>
                {product.best_promotion.description}
              </div>
            )}
          </div>

          {product.description && (
            <div className={styles.productDescription}>
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>
          )}

          <div className={styles.productDetails}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Stock disponible:</span>
              <span className={isOutOfStock ? styles.outOfStock : styles.inStock}>
                {product.stock_quantity > 0 ? `${product.stock_quantity} ${product.unit || 'kg'}` : 'Rupture de stock'}
              </span>
            </div>

            {product.unit && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Unité:</span>
                <span>{product.unit}</span>
              </div>
            )}

            {product.origin && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Origine:</span>
                <span>{product.origin}</span>
              </div>
            )}

            {product.variety && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Variété:</span>
                <span>{product.variety}</span>
              </div>
            )}

            {product.quality && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Qualité:</span>
                <span className={styles.qualityBadge} data-quality={product.quality}>
                  {product.quality === 'premium' ? 'Premium' : product.quality === 'standard' ? 'Standard' : 'Économique'}
                </span>
              </div>
            )}

            {product.weight && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Poids:</span>
                <span>{product.weight} kg</span>
              </div>
            )}
          </div>

          {!isOutOfStock && (
            <div className={styles.addToCartSection}>
              <div className={styles.quantitySelector}>
                <label>Quantité:</label>
                <div className={styles.quantityControls}>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={product.stock_quantity}
                    value={quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setQuantity(Math.max(1, Math.min(val, product.stock_quantity)));
                    }}
                  />
                  <button
                    onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                    disabled={quantity >= product.stock_quantity}
                  >
                    +
                  </button>
                </div>
                <span className={styles.unit}>{product.unit || 'kg'}</span>
              </div>

              <button
                className={`${styles.addToCart} ${addedToCart ? styles.added : ''}`}
                onClick={handleAddToCart}
                disabled={addedToCart}
              >
                {addedToCart ? '✓ Ajouté au panier' : 'Ajouter au panier'}
              </button>
            </div>
          )}

          {isOutOfStock && (
            <div className={styles.outOfStockMessage}>
              <p>Ce produit est actuellement en rupture de stock.</p>
            </div>
          )}
        </div>
      </div>

      {/* Section des avis */}
      {product && (
        <ProductReviews
          productId={product.id}
          productRating={product.rating}
          productReviewCount={product.review_count}
        />
      )}
    </div>
  );
};

export default ProductDetail;